import * as React from "react";
import Navbar from "../../Components/Navbar";
import { sendGetRequest, useGetRequest, sendPostRequest } from "../../Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button, Table, Modal } from "flowbite-react";
import { useState } from "react";
import CountryDropdown from "../../Components/CountryDropdown";
import { useNavigate } from "react-router-dom";
import { Notyf } from "notyf";
import "notyf/notyf.min.css"; // for React, Vue and Svelte
import Header from "../../Components/Header";
import Swal from "sweetalert2";
import dateFormat, { masks } from "dateformat";
import axios from "axios";

export default function AdvisorStatementsList() {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const domain = "https://money-trove.org";

  const notyf = new Notyf({
    position: {
      x: "right",
      y: "top",
    },
  });

  const addedNotyf = () => {
    notyf.success("User added");
  };

  // loading statements
  const { data: statements, isLoading } = useQuery({
    queryFn: () => sendGetRequest("advisor/statements/list", {}),
    queryKey: ["statements"],
  });

  function getAdvisorPDF(advisorStatementID) {
    window.open(
      `${domain}/advisor/statement/pdf?advisorStatementID=${encodeURIComponent(
        advisorStatementID
      )}`,
      "_blank"
    );
  }

  async function getAdvisorCSV(advisorStatementID) {
    const csvText = await sendGetRequest(
      `advisor/statement/csv?advisorStatementID=${advisorStatementID}`,
      {}
    );

    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `advisor-statement-${advisorStatementID}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const { mutateAsync: deleteStatement, isPending: isDeleting } = useMutation({
    mutationFn: (advisorStatementID) =>
      sendPostRequest("advisor/statement/delete", { advisorStatementID }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["statements"] });
      notyf.success("Statement deleted");
    },
    onError: (err) => {
      console.error(err);
      notyf.error("Could not delete statement");
    },
  });

  // confirm + delete
  async function handleDelete(advisorStatementID) {
    const res = await Swal.fire({
      title: "Delete this statement?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });

    if (res.isConfirmed) {
      await deleteStatement(advisorStatementID);
    }
  }

  if (!isLoading) {
    return (
      <>
        <Header />

        <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
          <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
            <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg">
              <div className="border-b border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between w-[94%] ml-[3%] mt-[3%] pb-6">
                <h3 className="text-base font-semibold text-gray-900">
                  Advisor Statements
                </h3>
              </div>

              <div className="p-6">
                {(statements?.statements?.length ?? 0) > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                          <th scope="col" className="px-4 py-3 w-1/4">
                            Date
                          </th>
                          <th scope="col" className="px-4 py-3 w-1/4">
                            Name
                          </th>
                          <th scope="col" className="px-4 py-3 w-1/4">
                            Total
                          </th>
                          <th scope="col" className="px-4 py-3 w-1/4"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {statements.statements.map((statement) => (
                          <tr
                            key={statement.advisorStatementID}
                            className="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <td className="px-4 py-2">
                              <div className="flex items-center text-sm font-medium text-gray-500">
                                {dateFormat(
                                  statement.createdAt,
                                  "mmmm dS, yyyy"
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-gray-700 dark:text-white">
                              {statement.firstName} {statement.surname}
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-gray-700 dark:text-white">
                              £{statement.total}
                            </td>
                            <td className="px-4 py-4">
                              <button
                                onClick={() =>
                                  handleDelete(statement.advisorStatementID)
                                }
                                disabled={isDeleting}
                                className="w-20 mr-2 h-10 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isDeleting ? "…" : "Delete"}
                              </button>
                              <button
                                onClick={() =>
                                  getAdvisorCSV(statement.advisorStatementID)
                                }
                                className="w-20 mr-2 h-10 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                              >
                                CSV
                              </button>
                              <button
                                onClick={() =>
                                  getAdvisorPDF(statement.advisorStatementID)
                                }
                                className="w-20 mr-2 h-10 rounded-md bg-slate-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-500"
                              >
                                PDF
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mx-auto max-w-xl">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-10 text-center">
                      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        No advisor statements yet
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        When you create advisor statements, they’ll appear here.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }
}
